/**
 * Every study block Alter has planned, as rows for the home screen.
 *
 * Allen, 2026-08-19: "also on the home page it should show you all of your
 * study sessions." Today's list above it answers "what am I doing now"; this
 * answers "what has Alter actually planned for me", which is the question you
 * ask before deciding whether the week is real.
 *
 * Pure, like `today.js` and for the same reason: no DOM, no fetch, no clock.
 * Everything arrives as an argument, so the ordering, the labelling and the
 * "is this behind me" judgement are all testable at their boundaries.
 *
 * ## Units
 *
 * `alter/publish.py` publishes epoch **seconds** on a block, the same as
 * everywhere else in this project. They become `Date`s here, once, at the
 * edge -- the mistake `today.js` records is what happens when three sources
 * with three units get sorted together untouched.
 */

const isNumber = (value) => typeof value === 'number' && Number.isFinite(value);

/** The browser's own zone, and a 12-hour clock, which is what Allen asked
 *  for everywhere he sees a time. */
function clock(date) {
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** `Today 1:10 pm`, or `Thu 1:10 pm` for any other day. */
export function whenLabel(start, now) {
  const sameDay = start.getFullYear() === now.getFullYear()
    && start.getMonth() === now.getMonth()
    && start.getDate() === now.getDate();
  return `${sameDay ? 'Today' : DAYS[start.getDay()]} ${clock(start)}`;
}

/**
 * Planned blocks into rows, soonest first.
 *
 * A block with no usable start is dropped rather than given one: a row whose
 * time was invented is a row that sorts into the wrong place and offers to
 * start a timer for the wrong hour.
 *
 * Blocks already behind you are kept and marked, not removed. One you did not
 * do is still a thing that was planned, and a list that quietly loses the
 * morning reads as a list that never had it.
 */
export function studyRows(blocks, now, { limit = 20 } = {}) {
  const rows = [];
  for (const block of blocks ?? []) {
    const startAt = block?.startAt;
    if (!isNumber(startAt)) continue;
    const start = new Date(startAt * 1000);
    if (Number.isNaN(start.getTime())) continue;
    const endAt = isNumber(block?.endAt) ? block.endAt : null;
    rows.push({
      id: String(block?.sessionId ?? block?.gcalEventId ?? startAt),
      title: String(block?.title || 'Study'),
      courseCode: String(block?.courseCode || ''),
      workItemId: block?.workItemId == null ? null : String(block.workItemId),
      minutes: isNumber(block?.plannedMinutes) ? Math.round(block.plannedMinutes)
        : (endAt ? Math.round((endAt - startAt) / 60) : 0),
      when: whenLabel(start, now),
      at: start.getTime(),
      // Half-open, like every other interval here: a block that ends exactly
      // now is behind you.
      past: (endAt ? endAt * 1000 : start.getTime()) <= now.getTime(),
      // The BLOCK's own state, not the assignment's. A sitting can be over
      // while the term paper it belongs to has weeks left, and `dayplan.py`
      // publishes both because they are different questions.
      done: block?.state === 'done',
    });
  }
  rows.sort((a, b) => a.at - b.at);
  return rows.slice(0, limit);
}
